import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/components/Toast';
import { Check, CreditCard, Calendar, AlertCircle, Crown, Shield, RefreshCw, Zap, Building2, XCircle, Clock, QrCode } from 'lucide-react';
import { PlanModulesList } from '@/components/PlanModulesList';
import { fetchTenantModules } from '@/lib/useTenantModules';
import { PaymentModal } from '@/components/PaymentModal';

export const SubscriptionPage = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [processing, setProcessing] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPlanForPayment, setSelectedPlanForPayment] = useState(null);
  
  const isSuperAdmin = user?.role === 'super_admin';

  const loadSubscription = async () => {
    if (isSuperAdmin) {
      setLoading(false);
      return;
    }
    try {
      const response = await api.get('/saas/subscription');
      setSubscription(response.data);
    } catch (error) {
      console.error('Erro ao carregar assinatura:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPlans = async () => {
    if (isSuperAdmin) return;
    try {
      const response = await api.get('/saas/plans');
      setAvailablePlans(response.data.plans || []);
    } catch (error) {
      console.error('Erro ao carregar planos:', error);
    }
  };

  useEffect(() => {
    loadSubscription();
    loadPlans();
  }, []);

  // Renovar assinatura - agora abre modal de pagamento
  const handleRenew = async () => {
    const currentPlan = subscription?.plan || availablePlans.find(p => p.id === subscription?.tenant?.plan_id);
    if (currentPlan) {
      setSelectedPlanForPayment(currentPlan);
      setShowPaymentModal(true);
    } else {
      toast.error('Plano não encontrado');
    }
  };

  // Fazer upgrade/trocar de plano - abre modal de pagamento
  const handleUpgrade = (plan) => {
    setSelectedPlanForPayment(plan);
    setShowPaymentModal(true);
  };

  // Callback quando pagamento é bem sucedido
  const handlePaymentSuccess = () => {
    toast.success('Pagamento confirmado! Seu plano foi ativado.');
    fetchTenantModules(true);
    loadSubscription();
    setShowPaymentModal(false);
    setSelectedPlanForPayment(null);
  };

  // Trocar de plano (apenas troca, sem pagamento - para downgrade ou teste)
  const handleChangePlan = async (planId) => {
    if (!window.confirm('Deseja trocar para este plano?')) return;
    
    setProcessing(true);
    try {
      await api.post('/saas/change-plan', { plan_id: planId });
      toast.success('Plano alterado com sucesso!');
      fetchTenantModules(true);
      loadSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao trocar plano');
    } finally {
      setProcessing(false);
    }
  };

  // Reativar assinatura vencida
  const handleReactivate = async () => {
    if (!window.confirm('Deseja reativar sua assinatura?')) return;
    
    setProcessing(true);
    try {
      await api.post('/saas/reactivate');
      toast.success('Assinatura reativada com sucesso!');
      loadSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao reativar assinatura');
    } finally {
      setProcessing(false);
    }
  };

  // Cancelar renovação automática
  const handleCancelAutoRenew = async () => {
    if (!window.confirm('Tem certeza que deseja cancelar a renovação automática? Sua assinatura permanecerá ativa até o fim do período atual.')) return;
    
    setProcessing(true);
    try {
      await api.post('/saas/cancel');
      toast.success('Renovação automática cancelada');
      loadSubscription();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cancelar renovação');
    } finally {
      setProcessing(false);
    }
  };

  // Se for super_admin, mostrar página especial
  if (isSuperAdmin) {
    return (
      <div className="p-8 max-w-7xl mx-auto" data-testid="subscription-page">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Administrador do Sistema
          </h1>
          <p className="text-slate-600">Você é o proprietário do SaaS</p>
        </div>
        
        <Card className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0">
          <CardHeader>
            <CardTitle className="flex items-center gap-3 text-2xl">
              <Crown className="w-8 h-8" />
              Super Administrador
            </CardTitle>
            <CardDescription className="text-amber-100">
              Acesso total e ilimitado ao sistema
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
              <div className="bg-white/20 rounded-lg p-4">
                <Shield className="w-8 h-8 mb-2" />
                <h3 className="font-semibold text-lg">Sem Limites</h3>
                <p className="text-sm text-amber-100">Produtos, usuários e vendas ilimitados</p>
              </div>
              <div className="bg-white/20 rounded-lg p-4">
                <CreditCard className="w-8 h-8 mb-2" />
                <h3 className="font-semibold text-lg">Sem Cobrança</h3>
                <p className="text-sm text-amber-100">Você é o proprietário da plataforma</p>
              </div>
              <div className="bg-white/20 rounded-lg p-4">
                <Calendar className="w-8 h-8 mb-2" />
                <h3 className="font-semibold text-lg">Sem Expiração</h3>
                <p className="text-sm text-amber-100">Acesso permanente ao sistema</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Gerenciar Plataforma</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button 
              onClick={() => window.location.href = '/admin'}
              className="w-full bg-slate-800 hover:bg-slate-700"
            >
              <Shield className="w-4 h-4 mr-2" />
              Painel Super Admin
            </Button>
            <p className="text-sm text-slate-500 text-center">
              Gerencie empresas, planos e assinaturas da plataforma
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return <div className="p-8">Carregando...</div>;
  }

  // Se não há assinatura, mostra os planos com pagamento direto
  if (!subscription) {
    return (
      <div className="p-8 max-w-7xl mx-auto" data-testid="subscription-page">
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Minha Assinatura</h1>
          <p className="text-slate-600">Escolha um plano e ative agora mesmo</p>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-8 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
          <p className="text-amber-900 text-sm">
            Você ainda não possui uma assinatura ativa. Escolha um plano abaixo e pague com PIX ou Cartão para liberar o sistema automaticamente.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {availablePlans.map((plan) => {
            const isPopular = plan.id === 'professional' || plan.id === 'profissional';
            return (
              <Card
                key={plan.id}
                className={`relative flex flex-col ${isPopular ? 'border-amber-500 border-2 shadow-xl' : 'border-slate-200 shadow-lg'}`}
                data-testid={`plan-card-${plan.id}`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-amber-500 text-white px-3 py-0.5 rounded-full text-xs font-semibold">MAIS POPULAR</span>
                  </div>
                )}
                <CardHeader className="text-center pt-8">
                  <CardTitle className="text-2xl" style={{ fontFamily: 'Manrope, sans-serif' }}>{plan.name}</CardTitle>
                  <div className="mt-3">
                    <span className="text-4xl font-bold">{formatCurrency(plan.price)}</span>
                    <span className="text-slate-500">/mês</span>
                  </div>
                </CardHeader>
                <CardContent className="flex-1">
                  <p className="text-xs uppercase tracking-wider text-slate-500 mb-3">Módulos liberados</p>
                  <PlanModulesList plan={plan} />
                </CardContent>
                <div className="p-6 pt-0">
                  <Button
                    onClick={() => handleUpgrade(plan)}
                    className={`w-full h-12 ${isPopular ? 'bg-amber-500 hover:bg-amber-600' : 'bg-emerald-600 hover:bg-emerald-700'}`}
                    data-testid={`subscribe-pay-${plan.id}`}
                  >
                    <CreditCard className="w-4 h-4 mr-2" />
                    Assinar e Pagar
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>

        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedPlanForPayment(null);
          }}
          plan={selectedPlanForPayment}
          availablePlans={availablePlans}
          onPaymentSuccess={handlePaymentSuccess}
        />
      </div>
    );
  }

  const { tenant, plan, days_remaining, usage } = subscription;
  const isExpired = tenant?.status === 'expired' || days_remaining <= 0;
  const isExpiringSoon = days_remaining <= 7 && days_remaining > 0;
  const isTrial = tenant?.status === 'trial';
  const isActive = tenant?.status === 'active';

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="subscription-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>Minha Assinatura</h1>
        <p className="text-slate-600">Gerencie seu plano e acompanhe o uso</p>
      </div>

      {/* Alerta de Expiração */}
      {isExpired && (
        <div className="bg-rose-50 border border-rose-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-6 h-6 text-rose-600" />
              <div>
                <p className="font-semibold text-rose-900">Sua assinatura expirou!</p>
                <p className="text-sm text-rose-700">Renove agora para continuar usando o sistema.</p>
              </div>
            </div>
            <Button 
              onClick={handleReactivate}
              disabled={processing}
              className="bg-rose-600 hover:bg-rose-700"
            >
              {processing ? 'Processando...' : 'Reativar Agora'}
            </Button>
          </div>
        </div>
      )}

      {isExpiringSoon && !isExpired && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <p className="text-amber-900">
                {isTrial
                  ? `Seu período de teste termina em ${days_remaining} dias.`
                  : `Sua assinatura vence em ${days_remaining} dias.`}
              </p>
            </div>
            <Button 
              onClick={handleRenew}
              disabled={processing}
              variant="outline"
              className="border-amber-500 text-amber-700 hover:bg-amber-100"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Renovar Agora
            </Button>
          </div>
        </div>
      )}

      {/* Cards de Status */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-slate-600">Plano Atual</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {plan?.name || 'Básico'}
            </div>
            <div className="mt-2">
              <span className="text-2xl font-semibold text-emerald-600">
                {formatCurrency(plan?.price || 0)}
              </span>
              <span className="text-slate-600">/mês</span>
            </div>
            {isTrial && (
              <div className="mt-3 inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                Trial ({days_remaining} dias)
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-slate-600">Período</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-5 h-5 text-slate-600" />
              <span className="font-semibold">
                {isExpired ? 'Expirado' : `${days_remaining} dias restantes`}
              </span>
            </div>
            {subscription.subscription?.current_period_end && (
              <p className="text-sm text-slate-600">
                {isExpired ? 'Expirou em: ' : 'Vence em: '}
                {new Date(subscription.subscription.current_period_end).toLocaleDateString('pt-BR')}
              </p>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium text-slate-600">Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              <div className={`w-3 h-3 rounded-full ${
                isActive ? 'bg-emerald-500' :
                isTrial ? 'bg-blue-500' :
                isExpired ? 'bg-rose-500' : 'bg-amber-500'
              }`} />
              <span className="font-semibold">
                {isActive ? 'Ativo' :
                 isTrial ? 'Período de Teste' :
                 isExpired ? 'Expirado' : 
                 tenant?.status || 'Desconhecido'}
              </span>
            </div>
            {!isExpired && (
              <Button 
                onClick={handleRenew}
                disabled={processing}
                variant="outline"
                size="sm"
                className="mt-3 w-full"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Renovar
              </Button>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Uso do Plano */}
      {usage && (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Uso do Plano</CardTitle>
            <CardDescription>Acompanhe o consumo dos recursos do seu plano</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">Produtos</span>
                  <span className="font-medium">
                    {usage.products || 0} / {plan?.max_products === -1 ? '∞' : plan?.max_products}
                  </span>
                </div>
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-emerald-500 rounded-full"
                    style={{ width: `${plan?.max_products === -1 ? 10 : Math.min(100, ((usage.products || 0) / plan?.max_products) * 100)}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">Usuários</span>
                  <span className="font-medium">
                    {usage.users || 0} / {plan?.max_users === -1 ? '∞' : plan?.max_users}
                  </span>
                </div>
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-blue-500 rounded-full"
                    style={{ width: `${plan?.max_users === -1 ? 10 : Math.min(100, ((usage.users || 0) / plan?.max_users) * 100)}%` }}
                  />
                </div>
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-slate-600">Vendas este mês</span>
                  <span className="font-medium">{usage.sales_this_month || 0} <span className="text-slate-400 font-normal">(sem limite)</span></span>
                </div>
                <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-amber-500 rounded-full"
                    style={{ width: '10%' }}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Trocar de Plano */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-500" />
            Trocar de Plano
          </CardTitle>
          <CardDescription>Faça upgrade ou downgrade do seu plano atual</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {availablePlans.map((p) => {
              const isCurrentPlan = p.id === plan?.id;
              return (
                <div 
                  key={p.id}
                  className={`border rounded-lg p-4 ${isCurrentPlan ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200'}`}
                >
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-semibold">{p.name}</h4>
                    {isCurrentPlan && (
                      <span className="text-xs bg-emerald-500 text-white px-2 py-0.5 rounded">Atual</span>
                    )}
                  </div>
                  <p className="text-2xl font-bold text-slate-800 mb-3">
                    {formatCurrency(p.price)}<span className="text-sm font-normal text-slate-500">/mês</span>
                  </p>
                  <div className="mb-4"><PlanModulesList plan={p} compact /></div>
                  {!isCurrentPlan && (
                    <Button 
                      onClick={() => p.price > 0 ? handleUpgrade(p) : handleChangePlan(p.id)}
                      disabled={processing}
                      variant={p.price > (plan?.price || 0) ? "default" : "outline"}
                      className={`w-full ${p.price > (plan?.price || 0) ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
                    >
                      {p.price > 0 && <QrCode className="w-4 h-4 mr-2" />}
                      {p.price > (plan?.price || 0) ? 'Fazer Upgrade' : 'Trocar'}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Informações da Empresa */}
      {tenant && (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-slate-600" />
              Dados da Empresa
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-slate-500">Nome da Empresa</p>
                <p className="font-semibold">{tenant.company_name}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Email</p>
                <p className="font-semibold">{tenant.owner_email}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Data de Cadastro</p>
                <p className="font-semibold">
                  {tenant.created_at ? new Date(tenant.created_at).toLocaleDateString('pt-BR') : '-'}
                </p>
              </div>
              <div>
                <p className="text-sm text-slate-500">Forma de Pagamento</p>
                <p className="font-semibold">{subscription.subscription?.payment_method || 'Não definido'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Gerenciar Assinatura */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-slate-600" />
            Gerenciar Assinatura
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Renovação automática */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium">Renovação Automática</p>
                <p className="text-sm text-slate-500">
                  {subscription.subscription?.auto_renew 
                    ? 'Sua assinatura será renovada automaticamente' 
                    : 'Renovação automática desativada'}
                </p>
              </div>
              <div className={`px-3 py-1 rounded-full text-sm font-medium ${
                subscription.subscription?.auto_renew 
                  ? 'bg-emerald-100 text-emerald-700' 
                  : 'bg-slate-200 text-slate-600'
              }`}>
                {subscription.subscription?.auto_renew ? 'Ativa' : 'Inativa'}
              </div>
            </div>

            {/* Ações */}
            <div className="flex flex-wrap gap-3">
              <Button 
                onClick={handleRenew}
                disabled={processing}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Renovar por +30 dias
              </Button>
              
              {subscription.subscription?.auto_renew && (
                <Button 
                  onClick={handleCancelAutoRenew}
                  disabled={processing}
                  variant="outline"
                  className="text-rose-600 border-rose-300 hover:bg-rose-50"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Cancelar Renovação Automática
                </Button>
              )}
            </div>

            {/* Aviso */}
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-700 flex items-start gap-2">
                <Clock className="w-4 h-4 mt-0.5 shrink-0" />
                <span>
                  Ao renovar, sua assinatura será estendida por mais 30 dias a partir da data de vencimento atual.
                  {!subscription.subscription?.auto_renew && ' Ative a renovação automática para não perder acesso.'}
                </span>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Modal de Pagamento */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);
          setSelectedPlanForPayment(null);
        }}
        plan={selectedPlanForPayment}
        availablePlans={availablePlans}
        onPaymentSuccess={handlePaymentSuccess}
      />
    </div>
  );
};
